import React from 'react';

import styled, { useTheme } from 'styled-components';
import { Theme } from 'styles/theme';
import usePcrV2 from '../hooks/usePcrV2';
import { SummaryGridRow } from '../version-history/VersionHistorySummaryGrid';
import {
  EpdVersionHistoryData,
  useEpdVersionHistoryDataOrWhileLoadingThenNull,
} from '../version-history/utils/EpdVersionHistoryDataContext';
import { useEpdComparedVersionHistoryDataOrWhileLoadingThenNull } from './EpdComparedVersionHistoryDataContext';
import { getRowColor } from './utils/getRowColor';
import { FieldPanelHeader } from 'styles/v2/Styles.styled';

export type ComparerRowsGetter = (data: EpdVersionHistoryData, isPcrV2: boolean, additionalData?: any) => any[];

const VersionCompareKeyValueGrid: React.FC<{
  isLeft: boolean;
  additionalData?: any;
  headerText?: string;
  rowsGetter: ComparerRowsGetter;
}> = ({ isLeft, rowsGetter, additionalData, headerText }) => {
  const theme = useTheme() as Theme;
  const epdVersionAHistoryData = useEpdVersionHistoryDataOrWhileLoadingThenNull();
  const epdVersionBHistoryData = useEpdComparedVersionHistoryDataOrWhileLoadingThenNull();
  const isPcrV2 = usePcrV2();

  const rowsA = epdVersionAHistoryData ? rowsGetter(epdVersionAHistoryData, isPcrV2, additionalData?.left) || [] : [];
  const rowsB = epdVersionBHistoryData ? rowsGetter(epdVersionBHistoryData, isPcrV2, additionalData?.right) || [] : [];

  type MergedRow = {
    nameA?: string;
    nameB?: string;
    valueA?: any;
    valueB?: any;
    ord: number;
    isHtmlRichText?: boolean;
  };

  const mergeRowsByNameWithOrder = (rowsA: SummaryGridRow[], rowsB: SummaryGridRow[]): MergedRow[] => {
    const map = new Map<string, MergedRow>();
    rowsA.forEach((r) => {
      map.set(r.name, { nameA: r.name, valueA: r.value, ord: r.ord ?? 0, isHtmlRichText: r.isHtmlRichText });
    });

    // Merge rows from B
    rowsB.forEach((r) => {
      const existing = map.get(r.name);
      if (existing) {
        existing.valueB = r.value;
        existing.ord = Math.min(existing.ord, r.ord ?? 0);
      } else {
        map.set(r.name, { nameB: r.name, valueB: r.value, ord: r.ord ?? 0, isHtmlRichText: r.isHtmlRichText });
      }
    });

    // Convert to array and sort by ord
    return Array.from(map.values()).sort((a, b) => a.ord - b.ord);
  };

  const [source, target] = isLeft ? [rowsA, rowsB] : [rowsB, rowsA];
  const rows = mergeRowsByNameWithOrder(source, target);

  if (rows.length == 0) return null;

  return (
    <>
      {headerText && <FieldPanelHeader>{headerText}</FieldPanelHeader>}
      <StyledVersionCompareGridTable border={1}>
        <tbody>
          {rows.map((row: MergedRow) => (
            <tr
              key={row.nameA || row.nameB}
              style={{
                border: `1px solid ${theme.colors.elementsInputBorder}`,
                backgroundColor: getRowColor(isLeft, row, theme),
              }}
            >
              <td>
                <div style={{ display: 'grid' }}>
                  <span style={{ gridArea: '1/1' }}>{row.nameA}</span>
                  <HiddenSpan>{row.nameB}</HiddenSpan>
                </div>
              </td>

              <td style={{ display: 'grid', border: '0' }}>
                <span style={{ gridArea: '1/1' }}>
                  {row.isHtmlRichText ? <ValueSpan dangerouslySetInnerHTML={{ __html: row.valueA }} /> : row.valueA}
                </span>
                <HiddenSpan>
                  {row.isHtmlRichText ? <ValueSpan dangerouslySetInnerHTML={{ __html: row.valueB }} /> : row.valueB}
                </HiddenSpan>
              </td>
            </tr>
          ))}
        </tbody>
      </StyledVersionCompareGridTable>
    </>
  );
};

export const HiddenSpan = styled.span`
  grid-area: 1 / 1;
  visibility: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  color: red;
`;

const StyledVersionCompareGridTable = styled.table`
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) => props.theme.colors.elementsInputBorder};
  ${(props) => props.theme.typography.contentTableBodyXs}
  tr > td {
    padding: 0.5rem 1rem;
    border-width: 1px;
    border-style: solid;
    border-color: ${(props) => props.theme.colors.elementsInputBorder};
    vertical-align: top;
    white-space: pre-wrap;
    word-break: break-word;
    min-width: 10rem;
  }
`;

const ValueSpan = styled.span`
  & > p,
  & > ul {
    margin: 0;
  }
`;

export default VersionCompareKeyValueGrid;
